import type { components } from '@fluencelabs/vodopad-admin-api'
import type { DataProvider, HttpError } from '@refinedev/core'

import client from '@/api/client'
import { unwrapApiData, unwrapApiResult } from '@/api/unwrapApiResult'
import { VODOPAD_ENDPOINT } from '@/config/runtime'
import { ensureArray } from '@/types/api'
import { serializeQueryWithArrayBrackets } from '@/utils/querySerializer'

/**
 * Custom Refine data provider wrapping openapi-fetch.
 *
 * All paginated endpoints use flat `page, perPage` query params.
 * Some endpoints have no pagination (invitations, clusters).
 * Non-standard mutations (bulk terminate, grant/charge) handled via useCustomMutation.
 */

type PaginationInfo = components['schemas']['PaginationInfo']

function extractPaginated<T>(data: { items: T[]; pagination: PaginationInfo }) {
  return {
    data: data.items,
    total: data.pagination.totalRecords ?? data.items.length,
  }
}

// Show pages render a 404 as "not found", so it must reach them as a status.
function unwrapRecord<TData, TError>(
  result: { response: Response; data?: TData; error?: TError },
  fallbackMessage: string,
) {
  if (result.response.status === 404) {
    const notFound: HttpError = { message: 'Not found', statusCode: 404 }
    throw notFound
  }
  return unwrapApiData(result, fallbackMessage)
}

// --- Typed list fetchers ---

async function listUsers(
  page: number,
  pageSize: number,
  filters: Record<string, unknown>,
) {
  const result = await client.GET('/v2/admin/users', {
    params: { query: { page, perPage: pageSize, ...filters } },
    querySerializer: serializeQueryWithArrayBrackets,
  })
  return extractPaginated(unwrapApiData(result, 'Failed to fetch users'))
}

async function listVms(
  page: number,
  pageSize: number,
  filters: Record<string, unknown>,
) {
  const result = await client.GET('/v2/admin/vms', {
    params: { query: { page, perPage: pageSize, ...filters } },
    querySerializer: serializeQueryWithArrayBrackets,
  })
  return extractPaginated(unwrapApiData(result, 'Failed to fetch VMs'))
}

async function listStorages(
  page: number,
  pageSize: number,
  filters: Record<string, unknown>,
) {
  const result = await client.GET('/v1/admin/storages', {
    params: { query: { page, perPage: pageSize, ...filters } },
    querySerializer: serializeQueryWithArrayBrackets,
  })
  return extractPaginated(unwrapApiData(result, 'Failed to fetch storages'))
}

async function listPublicIps(
  page: number,
  pageSize: number,
  filters: Record<string, unknown>,
) {
  const result = await client.GET('/v1/admin/public_ips', {
    params: { query: { page, perPage: pageSize, ...filters } },
    querySerializer: serializeQueryWithArrayBrackets,
  })
  return extractPaginated(unwrapApiData(result, 'Failed to fetch public IPs'))
}

async function listUserGroups(
  page: number,
  pageSize: number,
  filters: Record<string, unknown>,
) {
  const result = await client.GET('/v1/admin/user_groups', {
    params: { query: { page, perPage: pageSize, ...filters } },
    querySerializer: serializeQueryWithArrayBrackets,
  })
  return extractPaginated(unwrapApiData(result, 'Failed to fetch user groups'))
}

async function listInvitations(page: number, pageSize: number) {
  const result = await client.GET('/v1/admin/invites', {
    params: { query: { page, perPage: pageSize } },
  })
  return extractPaginated(unwrapApiData(result, 'Failed to fetch invitations'))
}

async function listPromoCodes(
  page: number,
  pageSize: number,
  filters: Record<string, unknown>,
) {
  const result = await client.GET('/v1/admin/promo_codes', {
    params: { query: { page, perPage: pageSize, ...filters } },
    querySerializer: serializeQueryWithArrayBrackets,
  })
  return extractPaginated(unwrapApiData(result, 'Failed to fetch promo codes'))
}

async function listVpcs(
  page: number,
  pageSize: number,
  filters: Record<string, unknown>,
) {
  const result = await client.GET('/v1/admin/vpcs', {
    params: { query: { page, perPage: pageSize, ...filters } },
    querySerializer: serializeQueryWithArrayBrackets,
  })
  return extractPaginated(unwrapApiData(result, 'Failed to fetch VPCs'))
}

async function listSubnets(
  page: number,
  pageSize: number,
  filters: Record<string, unknown>,
) {
  const result = await client.GET('/v1/admin/subnets', {
    params: { query: { page, perPage: pageSize, ...filters } },
    querySerializer: serializeQueryWithArrayBrackets,
  })
  return extractPaginated(unwrapApiData(result, 'Failed to fetch subnets'))
}

async function listClusters() {
  const result = await client.GET('/v1/clusters')
  const { items } = unwrapApiData(result, 'Failed to fetch clusters')
  return { data: items, total: items.length }
}

async function listPaymentIntents(
  page: number,
  pageSize: number,
  filters: Record<string, unknown>,
) {
  const result = await client.GET('/v2/admin/payments/intents', {
    // page/perPage are accepted at runtime (serde flatten) but utoipa marks
    // them `#[param(ignore)]`, so they're absent from the generated query type.
    // @ts-expect-error -- pagination params omitted from the typed query
    params: { query: { page, perPage: pageSize, ...filters } },
    querySerializer: serializeQueryWithArrayBrackets,
  })
  return extractPaginated(
    unwrapApiData(result, 'Failed to fetch payment intents'),
  )
}

async function listPaymentMethods() {
  const result = await client.GET('/v2/admin/payment-methods')
  const data = unwrapApiData(result, 'Failed to fetch payment methods')
  const items = ensureArray<components['schemas']['PaymentMethodDto']>(data)
  return { data: items, total: items.length }
}

// --- Filters helper ---

function buildFilters(
  filters?: Array<{ field: string; operator: string; value: unknown }>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  if (!filters) return result
  for (const filter of filters) {
    if (
      filter.value === undefined ||
      filter.value === null ||
      filter.value === ''
    )
      continue
    result[filter.field] = filter.value
  }
  return result
}

// --- Data Provider ---
// We use `as DataProvider` because Refine's generic TData variance makes it
// impossible to return concrete typed data from a DataProvider implementation.
// The actual function bodies are fully typed through openapi-fetch.

export const dataProvider = {
  getList: async ({
    resource,
    pagination,
    filters,
    meta,
  }: {
    resource: string
    pagination?: { currentPage?: number; pageSize?: number; mode?: string }
    filters?: Array<{ field: string; operator: string; value: unknown }>
    meta?: { params?: Record<string, unknown> }
  }) => {
    const page = pagination?.currentPage ?? 1
    const pageSize = pagination?.pageSize ?? 5
    const filterParams = buildFilters(filters)
    const allFilters = { ...filterParams, ...meta?.params }

    switch (resource) {
      case 'users':
        return listUsers(page, pageSize, allFilters)
      case 'vms':
        return listVms(page, pageSize, allFilters)
      case 'storages':
        return listStorages(page, pageSize, allFilters)
      case 'public-ips':
        return listPublicIps(page, pageSize, allFilters)
      case 'user-groups':
        return listUserGroups(page, pageSize, allFilters)
      case 'invitations':
        return listInvitations(page, pageSize)
      case 'promo-codes':
        return listPromoCodes(page, pageSize, allFilters)
      case 'vpcs':
        return listVpcs(page, pageSize, allFilters)
      case 'subnets':
        return listSubnets(page, pageSize, allFilters)
      case 'clusters':
        return listClusters()
      case 'payments':
        return listPaymentIntents(page, pageSize, allFilters)
      case 'payment-methods':
        return listPaymentMethods()
      default:
        throw new Error(`Unknown resource: ${resource}`)
    }
  },

  getOne: async ({
    resource,
    id,
  }: {
    resource: string
    id: string | number
  }) => {
    if (resource === 'users') {
      const result = await client.GET('/v1/admin/users/{user_id}', {
        params: { path: { user_id: String(id) } },
      })
      return { data: unwrapApiData(result, 'Failed to fetch user') }
    }
    if (resource === 'promo-codes') {
      const result = await client.GET('/v1/admin/promo_codes/{promo_code_id}', {
        params: { path: { promo_code_id: String(id) } },
      })
      return { data: unwrapApiData(result, 'Failed to fetch promo code') }
    }

    if (resource === 'vms') {
      const result = await client.GET('/v1/admin/vms/{vm_id}', {
        params: { path: { vm_id: String(id) } },
      })
      return { data: unwrapApiData(result, 'Failed to fetch VM') }
    }
    if (resource === 'public-ips') {
      const result = await client.GET('/v1/admin/public_ips/{public_ip_id}', {
        params: { path: { public_ip_id: String(id) } },
      })
      return { data: unwrapRecord(result, 'Failed to fetch public IP') }
    }
    if (resource === 'storages') {
      const result = await client.GET('/v1/admin/storages/{storage_id}', {
        params: { path: { storage_id: String(id) } },
      })
      return { data: unwrapRecord(result, 'Failed to fetch storage') }
    }
    if (resource === 'subnets') {
      const result = await client.GET('/v1/admin/subnets/{subnet_id}', {
        params: { path: { subnet_id: String(id) } },
      })
      return { data: unwrapRecord(result, 'Failed to fetch subnet') }
    }
    if (resource === 'vpcs') {
      const result = await client.GET('/v1/admin/vpcs/{vpc_id}', {
        params: { path: { vpc_id: String(id) } },
      })
      return { data: unwrapRecord(result, 'Failed to fetch VPC') }
    }
    if (resource === 'payments') {
      const result = await client.GET('/v2/admin/payments/intents/{id}', {
        params: { path: { id: String(id) } },
      })
      return { data: unwrapApiData(result, 'Failed to fetch payment intent') }
    }
    throw new Error(`getOne not supported for resource: ${resource}`)
  },

  create: async ({
    resource,
    variables,
  }: {
    resource: string
    variables: Record<string, unknown>
  }) => {
    switch (resource) {
      case 'invitations': {
        const result = await client.POST('/v1/admin/invites', {
          body: { email: variables.email as string },
        })
        return { data: unwrapApiData(result, 'Failed to create invitation') }
      }
      case 'user-groups': {
        const result = await client.POST('/v1/admin/user_groups', {
          body: variables as { name: string; description?: string },
        })
        return { data: unwrapApiData(result, 'Failed to create user group') }
      }
      case 'promo-codes': {
        const body =
          variables as components['schemas']['CreatePromoCodeRequest']
        const result = await client.POST('/v1/admin/promo_codes', { body })
        return { data: unwrapApiData(result, 'Failed to create promo code') }
      }
      case 'storages': {
        const result = await client.POST('/v1/admin/storages', {
          body: variables as {
            clusterId: string
            name: string
            osImage?: string | null
            replicated: boolean
            storageType: 'NVME'
            userId: string
            volumeGb: number
          },
        })
        return { data: unwrapApiData(result, 'Failed to create storage') }
      }
      case 'public-ips': {
        const result = await client.POST('/v1/admin/public_ips', {
          body: variables as {
            addressType: 'V4' | 'V6'
            clusterId: string
            name: string
            userId: string
          },
        })
        return { data: unwrapApiData(result, 'Failed to create public IP') }
      }
      case 'vms': {
        const result = await client.POST('/v2/admin/vms', {
          body: variables as {
            bootDiskId: string
            clusterId: string
            configurationId: string
            dataDisks?: string[]
            name: string
            publicIp?: string | null
            sshKeys?: string[]
            userId: string
          },
        })
        return { data: unwrapApiData(result, 'Failed to deploy VM') }
      }
      default:
        throw new Error(`create not supported for resource: ${resource}`)
    }
  },

  update: async () => {
    throw new Error(
      'Standard update not supported. Use useCustomMutation for specific actions.',
    )
  },

  deleteOne: async ({
    resource,
    id,
    meta,
  }: {
    resource: string
    id: string | number
    meta?: Record<string, unknown>
  }) => {
    if (resource === 'invitations') {
      const email = (meta?.email as string) ?? String(id)
      const result = await client.DELETE('/v1/admin/invites', {
        body: { email },
      })
      unwrapApiData(result, 'Failed to delete invitation')
      return { data: { id: email } }
    }
    throw new Error(`deleteOne not supported for resource: ${resource}`)
  },

  custom: async ({
    url,
    method,
    query,
  }: {
    url: string
    method: string
    query?: Record<string, unknown>
  }) => {
    const baseUrl = VODOPAD_ENDPOINT
    const path = url.startsWith(baseUrl) ? url.slice(baseUrl.length) : url

    // We cast paths since custom() receives dynamic URLs not known at compile time.
    // The auth middleware on the client handles authentication.
    type GetPath = '/v1/clusters'

    if (method === 'get') {
      const options = query ? { params: { query } } : undefined
      // @ts-expect-error -- dynamic custom() paths not in OpenAPI spec
      const result = await client.GET(path as GetPath, options)
      unwrapApiResult(result, 'Request failed')
      return { data: result.data ?? {} }
    }
    throw new Error(`Unsupported method: ${method}`)
  },

  getApiUrl: () => VODOPAD_ENDPOINT,
} as DataProvider
